import React, { useEffect, useState } from "react";
import { Modal } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import CreateAddressSelectionGuideForm from "~/components/Forms/CreateAddressSelectionGuideForm";
import Preloader from "~/components/Preloader";
import { useGeoPlace } from "~/entity/Geo";
import type { IGeoPlaceItem } from "~/entity/Geo/model/shema";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { useNotify } from "~/shared/hooks/useNotify";

const addressSelectionGuideFormId = "AddressSelectionGuideForm";

enum ModalState {
  edit = "Edit",
  create = "Create",
}

interface IAddressSelectionGuideModal {
  reFetch: () => void;
  geoPlaces: IGeoPlaceItem[];
}

const AddressSelectionGuideModal = ({
  reFetch,
  geoPlaces,
}: IAddressSelectionGuideModal) => {
  const { t } = useTranslation();

  const { getGeoPlaceById, addGeoPlace, setGeoPlace } = useGeoPlace();

  const { openModal, currentId } = useGetLocationData();
  const setDataReplace = useSetLocationData(true);
  const isOpen =
    openModal === ModalState.create || openModal === ModalState.edit;

  const { notifySuccess } = useNotify();

  const [currentAddress, setCurrentAddress] = useState(null);
  const [loading, setLoading] = useState(false);

  const closeModal = () => setDataReplace("openModal", "", "currentId", "");

  const onSubmit = async (data) => {
    try {
      if (currentId) {
        await setGeoPlace({ ...data, id: Number(currentId) });

        notifySuccess(t("addressSelectionGuide.editAddress"));
      } else {
        await addGeoPlace(data);

        notifySuccess(t("addressSelectionGuide.createAddress"));
      }

      reFetch();
      closeModal();
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!currentId) return;

    setLoading(true);

    getGeoPlaceById(currentId)
      .then((res) => {
        setCurrentAddress(res.data);
      })
      .finally(() => setLoading(false));

    return () => {
      setCurrentAddress(null);
    };
  }, [currentId]);

  const filteredPlaces =
    currentAddress ||
    geoPlaces.find((item) => String(item.id) === String(currentId));

  return (
    <Modal
      open={isOpen}
      title={t("addressSelectionGuide.address_title")}
      onCancel={closeModal}
      closable
      centered
      destroyOnHidden
      width={996}
      okButton={{
        htmlType: "submit",
        form: addressSelectionGuideFormId,
        children: t("ui.save"),
      }}
    >
      {currentId && loading ? (
        <Preloader />
      ) : (
        <CreateAddressSelectionGuideForm
          formId={addressSelectionGuideFormId}
          onSubmitHandler={onSubmit}
          instance={filteredPlaces}
        />
      )}
    </Modal>
  );
};

export default AddressSelectionGuideModal;
